import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Users, Star, Clock, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useConversionTracking } from '@/components/analytics/ConversionTracking';

const PatientConversionCTA: React.FC = () => {
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();
  const { trackButtonClick } = useConversionTracking();
  
  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="relative overflow-hidden group hover:shadow-2xl transition-all border-2 border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5" />
            <CardContent className="relative p-8 md:p-12 space-y-8">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6 mx-auto">
                  <Users className="w-10 h-10 text-primary" />
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Start Your Mental Health Journey?
                </h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Connect with certified therapists worldwide and find the perfect match for your needs
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div className="text-center p-4 bg-background rounded-lg">
                  <Star className="w-8 h-8 text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold mb-1">5,000+</div>
                  <div className="text-sm text-muted-foreground">Certified Therapists</div>
                </div>
                <div className="text-center p-4 bg-background rounded-lg">
                  <Users className="w-8 h-8 text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold mb-1">40+</div>
                  <div className="text-sm text-muted-foreground">Languages</div>
                </div>
                <div className="text-center p-4 bg-background rounded-lg">
                  <Clock className="w-8 h-8 text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold mb-1">24/7</div>
                  <div className="text-sm text-muted-foreground">Availability</div>
                </div>
                <div className="text-center p-4 bg-background rounded-lg">
                  <Shield className="w-8 h-8 text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold mb-1">100%</div>
                  <div className="text-sm text-muted-foreground">Secure & Private</div>
                </div>
              </div>

              <div className="space-y-4 mb-8 max-w-2xl mx-auto">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <p className="text-base">Sessions from $25 - choose affordable therapists in your language</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <p className="text-base">First consultation often free or heavily discounted</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <p className="text-base">Online or in-person - flexible to fit your schedule</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <p className="text-base">All therapists certified by Dr. José Luis Marín's Master program</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  onClick={() => {
                    trackButtonClick('patient-cta-find-therapist');
                    navigate(getLocalizedPath('/find'));
                  }}
                  className="text-lg py-6 px-8 group/btn"
                >
                  Find Your Therapist Now
                  <ArrowRight className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
                
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    trackButtonClick('patient-cta-how-it-works');
                    navigate(getLocalizedPath('/how-it-works'));
                  }}
                  className="text-lg py-6 px-8"
                >
                  Learn How It Works
                </Button>
              </div>
              
              <p className="text-sm text-center text-muted-foreground mt-6">
                No subscriptions • Pay only for sessions you book • Cancel anytime
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PatientConversionCTA;
